import React from "react";
import { X } from "lucide-react";

interface Props {
    sale: any | null;
    allSales?: any[];
    onClose: () => void;
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{children}</h3>
);

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="space-y-1">
        <div className="text-[11px] uppercase tracking-wide text-gray-600 font-medium">{label}</div>
        <div className="text-sm text-gray-200 break-all">{value ?? <span className="text-gray-600">—</span>}</div>
    </div>
);

const Badge: React.FC<{ value: string; isExchangeReturn?: boolean; isCancellationRefund?: boolean }> = ({
    value,
    isExchangeReturn = false,
    isCancellationRefund = false,
}) => {
    // ✅ Exchange Return (ReturnedForExchange) -> Purple Refund Badge
    if (value === "ReturnedForExchange" || isExchangeReturn) {
        return (
            <span className="inline-flex px-2 py-0.5 text-xs rounded border font-medium bg-purple-950 text-purple-400 border-purple-900">
                Refund
            </span>
        );
    }

    // ✅ Order Cancellation Refund -> Orange Refund Badge
    if (isCancellationRefund) {
        return (
            <span className="inline-flex px-2 py-0.5 text-xs rounded border font-medium bg-orange-950 text-orange-400 border-orange-900">
                Refund
            </span>
        );
    }

    const color =
        value === "cancelled" || value === "unsold"
            ? "bg-red-950 text-red-400 border-red-900"
            : value === "exchanged"
              ? "bg-blue-950 text-blue-400 border-blue-900"
              : value === "created"
                ? "bg-green-950 text-green-400 border-green-900"
                : value === "active"
                  ? "bg-green-950 text-green-400 border-green-900"
                  : "bg-[#1a1a1a] text-gray-400 border-[#2d2d2d]";

    return (
        <span className={`inline-flex px-2 py-0.5 text-xs rounded border font-medium capitalize ${color}`}>
            {value}
        </span>
    );
};

const BoolBadge: React.FC<{ value: boolean | null | undefined }> = ({ value }) => {
    if (value === true) {
        return (
            <span className="inline-flex px-2 py-0.5 text-xs rounded border font-medium bg-green-950 text-green-400 border-green-900">
                True
            </span>
        );
    }
    if (value === false) {
        return (
            <span className="inline-flex px-2 py-0.5 text-xs rounded border font-medium bg-red-950 text-red-400 border-red-900">
                False
            </span>
        );
    }
    return <span className="text-gray-600">—</span>;
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-[#151515] border border-[#2d2d2d] rounded p-4 h-full">
        <SectionTitle>{title}</SectionTitle>
        <div className="space-y-4">{children}</div>
    </div>
);

const isExchangeReturn = (sale: any): boolean => sale?.status === "ReturnedForExchange";

const isCancellationReversal = (sale: any): boolean => {
    const carePrice = parseFloat(sale?.carePrice ?? 0);

    if (!(carePrice < 0 && sale?.sold === false)) return false;
    if (sale?.status === "ReturnedForExchange") return false;

    return sale?.status === "created" || sale?.status === "cancelled";
};

function isCarePlusReplacement(item: any, allItems: any[]): boolean {
    if (item?.status !== "created" || item?.sold !== true) return false;

    const hasLink =
        (item?.eventId != null && item.eventId !== "") ||
        (item?.rootUniqueId != null && item.rootUniqueId !== "");
    if (!hasLink) return false;

    const originalExchanged =
        (item.eventId &&
            allItems.find((i: any) => i?.eventId === item.eventId && i?.status === "exchanged")) ||
        allItems.find(
            (i: any) =>
                i?.status === "exchanged" &&
                (i?.uniqueId === item.rootUniqueId || i?.rootUniqueId === item.rootUniqueId),
        );

    return Boolean(originalExchanged && originalExchanged.sold === true);
}

const SaleDetailModal: React.FC<Props> = ({ sale, allSales = [], onClose }) => {
    React.useEffect(() => {
        if (!sale) return;

        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handler);
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", handler);
            document.body.style.overflow = "unset";
        };
    }, [sale, onClose]);

    if (!sale) return null;

    const isExchReturn = isExchangeReturn(sale);
    const isCancelRefund = isCancellationReversal(sale);

    const fmtPrice = (v: string | number | null) => {
        if (v == null || v === "" || Number.isNaN(Number(v))) return null;
        const num = Number(v);
        if (num < 0) {
            return <span className="text-orange-400">-₹{Math.abs(num).toLocaleString("en-IN")}</span>;
        }
        return `₹${num.toLocaleString("en-IN")}`;
    };

    const fmtDate = (v: string | null) =>
        v
            ? new Date(v).toLocaleString("en-IN", {
                  dateStyle: "medium",
              })
            : null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
            <div
                className="relative w-full max-w-7xl bg-[#111111] border border-[#2d2d2d] rounded-lg max-h-[90vh] flex flex-col shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#2d2d2d] flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-white">Sale Details</span>
                        <Badge
                            value={sale.status ?? "unknown"}
                            isExchangeReturn={isExchReturn}
                            isCancellationRefund={isCancelRefund}
                        />
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-200 rounded transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 p-5 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 items-stretch">
                        {/* Column 1 — Identifiers */}
                        <SectionCard title="Identifiers">
                            <Field label="Care+ ID" value={<span className="font-mono">{sale.id}</span>} />
                            <Field label="Invoice ID" value={<span className="font-mono">{sale.uniqueId}</span>} />
                            <Field
                                label="Member UUID"
                                value={sale.memberUuid ? <span className="font-mono text-xs">{sale.memberUuid}</span> : null}
                            />
                            <Field label="Source" value={sale.source} />
                            {sale.eventId && <Field label="Event ID" value={<span className="font-mono text-xs">{sale.eventId}</span>} />}
                        </SectionCard>

                        {/* Column 2 — Customer */}
                        <SectionCard title="Customer">
                            <Field label="Name" value={sale.customerName} />
                            <Field label="Phone" value={<span className="font-mono">{sale.phoneNo}</span>} />
                            <Field label="Email" value={sale.email} />
                            <Field label="Address" value={sale.address} />
                        </SectionCard>

                        {/* Column 3 — Product */}
                        <SectionCard title="Product">
                            <Field label="Brand" value={sale.brand} />
                            <Field label="Product Name" value={sale.productName} />
                            <Field label="Product Code" value={sale.product !== sale.productName ? sale.product : null} />
                            <Field label="Color" value={sale.color} />
                            <Field label="Size" value={sale.size} />
                            <Field label="Size Type" value={sale.sizeType && sale.sizeType !== "-" ? sale.sizeType : null} />
                            <Field label="Gender" value={sale.gender} />
                        </SectionCard>

                        {/* Column 4 — Pricing */}
                        <SectionCard title="Pricing">
                            <Field label="Product Price" value={fmtPrice(sale.productPrice) ?? <span className="text-gray-600">—</span>} />
                            <Field label="Care+ Price" value={fmtPrice(sale.carePrice) ?? <span className="text-gray-600">—</span>} />
                            <Field label="Currency" value={sale.currencyCode} />
                        </SectionCard>

                        {/* Column 5 — Status & Dates */}
                        <SectionCard title="Status & Dates">
                            <Field label="Sold" value={<BoolBadge value={sale.sold} />} />

                            <Field
                                label="Opt In"
                                value={
                                    isExchReturn ? (
                                        <span className="inline-flex px-2 py-0.5 text-xs rounded border font-medium bg-purple-950 text-purple-400 border-purple-900">
                                            Returned For Exchange
                                        </span>
                                    ) : isCancelRefund ? (
                                        <span className="inline-flex px-2 py-0.5 text-xs rounded border font-medium bg-orange-950 text-orange-400 border-orange-900">
                                            Refund
                                        </span>
                                    ) : sale.status === "cancelled" ? (
                                        <span className="text-gray-400 text-xs font-medium">Cancelled</span>
                                    ) : !sale.sold ? (
                                        <span className="text-red-400 text-xs font-medium">False</span>
                                    ) : isCarePlusReplacement(sale, allSales) ? (
                                        <span className="inline-flex px-2 py-0.5 text-xs rounded border font-medium bg-blue-950 text-blue-400 border-blue-900">
                                            Replacement
                                        </span>
                                    ) : (
                                        <span className="text-green-400 text-xs font-medium">True</span>
                                    )
                                }
                            />

                            <Field
                                label="Status"
                                value={
                                    <Badge
                                        value={sale.status ?? "—"}
                                        isExchangeReturn={isExchReturn}
                                        isCancellationRefund={isCancelRefund}
                                    />
                                }
                            />
                            <Field label="Purchase Date" value={fmtDate(sale.transactionDate)} />
                            <Field label="Created At" value={fmtDate(sale.createdAt)} />
                        </SectionCard>
                    </div>

                    {sale.csvFileUrl && (
                        <div className="bg-[#151515] border border-[#2d2d2d] rounded p-4">
                            <SectionTitle>Source File</SectionTitle>
                            <a
                                href={sale.csvFileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors break-all"
                            >
                                {sale.csvFileUrl.split("/").pop()}
                            </a>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-[#2d2d2d] flex justify-end flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SaleDetailModal;